import { createContext, memo, useContext, useMemo } from 'react';

import type { FCWithChildren } from '../../../shared/utils/component.interface';
import type { IEvent } from '../../interfaces/events.interface';

export type EventHandler = (event: IEvent) => Promise<void> | boolean | undefined;

export interface EventingServiceBus {
    listeners: Array<EventHandler>;
    addEventListener: (handler: EventHandler) => void;
    removeEventListener: (handler: EventHandler) => void;
    publishEvent: (event: IEvent) => boolean;
}

export const EventingServiceContext = createContext<EventingServiceBus>({
    listeners: [],
    addEventListener: () => undefined,
    removeEventListener: () => undefined,
    publishEvent: () => false,
});

export const EventingServiceProvider: FCWithChildren<unknown> = memo(({ children }) => {
    const EventBus = useMemo<EventingServiceBus>(
        () => ({
            listeners: [],
            addEventListener(handler: EventHandler) {
                this.listeners.push(handler);
            },
            removeEventListener(handler: EventHandler) {
                const index = this.listeners.indexOf(handler);
                this.listeners.splice(index, 1);
            },
            publishEvent(event: IEvent) {
                return this.listeners.reduce(
                    (isHandled, handler) => (handler(event) === true ? true : isHandled),
                    false,
                );
            },
        }),
        [],
    );

    return (
        <EventingServiceContext.Provider value={EventBus}>
            {children}
        </EventingServiceContext.Provider>
    );
});

export function useEventBus() {
    const serviceBus = useContext(EventingServiceContext);

    return serviceBus;
}

export function useEventDispatcher() {
    const serviceBus = useEventBus();

    return useMemo(() => {
        return (event: IEvent) => {
            return serviceBus.publishEvent(event);
        };
    }, [serviceBus]);
}
